import React, { useState, useEffect, useRef } from 'react';
import API from '../http/API';
import { useRequestUrl, useAuthToken, useCurrentUser } from '../context/contextHooks';
import { useFinalizeLogEntry } from '../customHooks/useFinalizeLogEntry';
import { getCurrency, getDecimal } from '../config/utils';
import { useSxStyles as sx } from '../styles/sxStyles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid2 from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ReplayIcon from '@mui/icons-material/Replay';
import SendTwoToneIcon from '@mui/icons-material/SendTwoTone';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';

const SESSION_TIMEOUT_MS = 15 * 60 * 1000;

const ManageWidget = (props) => {
    const openDialog = props.openDialog;
    const setOpenDialog = props.setOpenDialog;
    const paymentRequest = props.paymentRequest;
    const setPaymentRequest = props.setPaymentRequest;
    const setPaymentResult = props.setPaymentResult;
    const court = props.court;
    const feeResponse = props.feeResponse;
    const cancelPymt = props.cancelPymt;

    const [requestUrl] = useRequestUrl();
    const [authToken] = useAuthToken();
    const [user] = useCurrentUser();
    const finalizeLogEntry = useFinalizeLogEntry();

    const theme = useTheme();
    const tiny = useMediaQuery(theme.breakpoints.down('sm'));

    const widg = useRef(null);
    const paymentRequestRef = useRef(null);
    const timerRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [noSubmit, setNoSubmit] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [declineMessage, setDeclineMessage] = useState(null);
    const [actionResponse, setActionResponse] = useState(null);
    const [scriptUrl, setScriptUrl] = useState(null);
    const [clientId, setClientId] = useState(null);
    const [bundleResponse, setBundleResponse] = useState(null);
    const [sessionExpired, setSessionExpired] = useState(false);

    const supportsAch = court?.onlineCardProcessor?.processor?.supportsAch === true;
    const widgetLayout = (court?.noChex !== true && supportsAch) ? 'standard:multi' : 'standard:cardonly';

    const clearMessages = () => {
        setErrorMessage(null);
        setDeclineMessage(null);
        setActionResponse(null);
    };

    const startSessionTimer = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            setBundleResponse(null);
            setSessionExpired(true);
        }, SESSION_TIMEOUT_MS);
    };

    const stopSessionTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const closeAndCancel = () => {
        stopSessionTimer();
        clearMessages();
        setBundleResponse(null);
        setSessionExpired(false);
        if (cancelPymt) cancelPymt();
        setOpenDialog(false);
    };

    const getScriptUrl = () => {
        clearMessages();
        setLoading(true);
        API.getRetrieve(`${requestUrl}/getNeumoScriptUrl`, 'getScriptUrl', `Bearer ${authToken}`, setActionResponse);
    };

    const getPaymentToken = (request) => {
        clearMessages();
        setLoading(true);
        API.postRetrieve(`${requestUrl}/getPaymentToken`, JSON.stringify(request), 'getPaymentToken', `Bearer ${authToken}`, setActionResponse);
    };

    const storePayment = (submitResponse) => {
        clearMessages();
        setLoading(true);
        const data = { paymentRequest: paymentRequestRef.current, submitResponse };
        API.postRetrieve(`${requestUrl}/storeWidgetPayment`, JSON.stringify(data), 'storeWidgetPayment', `Bearer ${authToken}`, setActionResponse);
    };

    const checkPaymentStatus = async () => {
        await new Promise(resolve => setTimeout(resolve, 10000));
        clearMessages();
        setLoading(true);
        API.postRetrieve(`${requestUrl}/checkPaymentStatus`, JSON.stringify(bundleResponse), 'checkPaymentStatus', `Bearer ${authToken}`, setActionResponse);
    };

    const handleWidgetError = (submitResponse) => {
        if (submitResponse?.paymentError?.message) {
            if (submitResponse.paymentError.message.toLowerCase().includes('unknown')) {
                checkPaymentStatus();
            } else {
                setDeclineMessage(submitResponse.paymentError.message);
                setNoSubmit(false);
            }
        } else if (submitResponse?.message) {
            const msg = submitResponse.message.toLowerCase();
            if (msg.includes('unknown') || msg.includes('again later')) {
                checkPaymentStatus();
            } else {
                const idx = submitResponse.message.indexOf('{');
                if (idx > -1) {
                    try {
                        const errorObj = JSON.parse(submitResponse.message.substring(idx));
                        setErrorMessage(errorObj?.detail ?? submitResponse.message.substring(idx));
                    } catch {
                        setErrorMessage(submitResponse.message);
                    }
                } else {
                    setErrorMessage(submitResponse.message);
                }
                setNoSubmit(false);
            }
        } else {
            checkPaymentStatus();
        }
    };

    const submitPayment = async () => {
        if (bundleResponse && widg?.current) {
            setNoSubmit(true);
            try {
                const submitResponse = await widg.current.submit(bundleResponse.submitToken);
                if (submitResponse?.transactionSuccessful) {
                    storePayment(submitResponse);
                } else if (submitResponse?.paymentError || submitResponse?.message) {
                    handleWidgetError(submitResponse);
                } else {
                    checkPaymentStatus();
                }
            } catch (error) {
                handleWidgetError(error);
            }
        } else {
            setErrorMessage('Payment information is not complete; please close this window and try again');
        }
    };

    const handleSubmit = () => {
        if (noSubmit) return;
        clearMessages();
        submitPayment();
    };

    useEffect(() => {
        if (actionResponse && loading) {
            if (actionResponse.error) {
                setNoSubmit(false);
                setErrorMessage(actionResponse.errorMessage);
            } else if (actionResponse.requestType === 'getScriptUrl') {
                setScriptUrl(actionResponse.data?.scriptUrl);
                setClientId(actionResponse.data?.clientId);
            } else if (actionResponse.requestType === 'getPaymentToken') {
                if (actionResponse.data) {
                    setBundleResponse(actionResponse.data);
                    startSessionTimer();
                } else {
                    setErrorMessage('Payment information is not complete; please close this window and try again');
                }
            } else if (actionResponse.requestType === 'storeWidgetPayment' || actionResponse.requestType === 'checkPaymentStatus') {
                setNoSubmit(false);
                const paymentData = actionResponse.data;
                if (paymentData?.authorized) {
                    stopSessionTimer();
                    finalizeLogEntry(paymentData, court, setActionResponse);
                    setPaymentResult(paymentData);
                    setOpenDialog(false);
                } else {
                    if (paymentData?.declineMessage) {
                        setDeclineMessage(paymentData.declineMessage);
                        if (paymentData) finalizeLogEntry(paymentData, court, setActionResponse);
                    } else {
                        setErrorMessage(paymentData?.errorMessage ?? actionResponse.errorMessage ?? 'An error occurred processing the payment; please try again or contact Customer Service');
                    }
                }
            }
            setLoading(false);
        }
    }, [actionResponse, loading]);

    useEffect(() => {
        if (!openDialog) return;
        clearMessages();
        getScriptUrl();
        return () => {
            stopSessionTimer();
        };
    }, [openDialog]);

    useEffect(() => {
        if (!scriptUrl) return;
        const existing = document.querySelector(`script[src="${scriptUrl}"]`);
        let script;
        if (!existing) {
            script = document.createElement('script');
            script.src = scriptUrl;
            script.type = 'module';
            script.async = true;
            document.body.appendChild(script);
        }
        const request = {
            ...paymentRequest,
            totalAmount: parseFloat(getDecimal(feeResponse?.totalAmount, false)),
            courtAmount: parseFloat(getDecimal(feeResponse?.courtAmount, false)),
            feeAmount: parseFloat(getDecimal(feeResponse?.citepayFee, false)),
            paymentOperator: user?.externalId,
            retryToken: crypto.randomUUID()
        };
        paymentRequestRef.current = request;
        if (setPaymentRequest) setPaymentRequest(request);
        getPaymentToken(request);
        return () => {
            if (script) document.body.removeChild(script);
        };
    }, [scriptUrl]);

    return (
        <Dialog
            open={openDialog}
            maxWidth='md'
            fullWidth
            sx={sx.dialog}
            slotProps={{ paper: { sx: sx.listContainer } }}
        >
            <DialogTitle color='primary'>Enter Payment Information</DialogTitle>
            <Backdrop sx={sx.backdrop} open={loading || noSubmit}>
                <CircularProgress color='inherit' />
            </Backdrop>
            <DialogContent dividers sx={sx.listContainer}>
                { errorMessage &&
                    <Alert severity='error' sx={{ marginBottom: '1rem', textAlign: 'left' }}>
                        <AlertTitle>Error!</AlertTitle>
                        {errorMessage}
                    </Alert>
                }
                { declineMessage &&
                    <Alert severity='warning' sx={{ marginBottom: '1rem', textAlign: 'left' }}>
                        {declineMessage}
                    </Alert>
                }
                { sessionExpired &&
                    <Alert severity='info' sx={{ marginBottom: '1rem', textAlign: 'left' }}>
                        <AlertTitle>Session expired</AlertTitle>
                        Your payment session has expired. Please close this window and re-select your payment items.
                    </Alert>
                }
                { bundleResponse && !sessionExpired &&
                    <Paper elevation={3} sx={{ padding: '1rem' }}>
                        <Stack alignItems='center' spacing={1}>
                            <Grid2 container spacing={1} sx={{ width: '100%' }}>
                                <Grid2 size={{ xs: 6, sm: 4 }}>
                                    <Typography sx={{ fontWeight: 'bold', textAlign: 'right' }}>Item(s) Subtotal:</Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 6, sm: 2 }}>
                                    <Typography color='textPrimary'>{getCurrency(feeResponse?.courtAmount)}</Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 6, sm: 4 }}>
                                    <Typography color='primary' sx={{ fontWeight: 'bold', textAlign: 'right' }}>Processing Fee:</Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 6, sm: 2 }}>
                                    <Typography color='textPrimary'>{getCurrency(feeResponse?.citepayFee)}</Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 6, sm: 4 }}>
                                    <Typography sx={{ fontWeight: 'bold', textAlign: 'right' }}>Payment Total:</Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 6, sm: 2 }}>
                                    <Typography color='textPrimary'>{getCurrency(feeResponse?.totalAmount)}</Typography>
                                </Grid2>
                            </Grid2>
                            <Box sx={{ width: '100%', px: 1 }}>
                                <payment-instrument
                                    id='pi'
                                    ref={widg}
                                    client-id={clientId}
                                    layout={widgetLayout}
                                    class='payment-instrument'
                                    style={{ width: '100%' }}
                                />
                            </Box>
                        </Stack>
                    </Paper>
                }
            </DialogContent>
            <DialogActions>
                <Box component='div' sx={{ display: 'flex', flexDirection: 'row-reverse', margin: '1rem' }}>
                    <Button
                        variant='contained'
                        color='primary'
                        startIcon={tiny ? null : <SendTwoToneIcon />}
                        disabled={!bundleResponse || noSubmit || sessionExpired}
                        sx={sx.squashedButton}
                        onClick={handleSubmit}
                    >
                        Submit
                    </Button>
                    <Button
                        variant='outlined'
                        color='primary'
                        startIcon={tiny ? null : <ReplayIcon />}
                        sx={sx.squashedButton}
                        onClick={closeAndCancel}
                        disabled={noSubmit}
                    >
                        Cancel
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default ManageWidget;
